import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Contact } from '@mayksel/types';
import type { PaginatedResponse } from '@mayksel/types';
import type { CreateContactInput, UpdateContactInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export interface ContactFilters {
  search?: string;
  companyId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useContacts(filters: ContactFilters = {}) {
  const { getToken, workspaceId } = useApiOpts();
  const { search, companyId, page = 1, limit = 50, sortBy = 'last_name', sortDir = 'asc' } = filters;

  return useQuery<PaginatedResponse<Contact>>({
    queryKey: ['contacts', workspaceId, search, companyId, page, limit, sortBy, sortDir],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (companyId) params.set('companyId', companyId);
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', sortBy ?? 'last_name');
      params.set('sortDir', sortDir ?? 'asc');
      const opts: { token?: string; workspaceId?: string } = {};
      if (token) opts.token = token;
      if (workspaceId) opts.workspaceId = workspaceId;
      return api.get<PaginatedResponse<Contact>>(`/v1/contacts?${params.toString()}`, opts);
    },
    enabled: !!workspaceId,
  });
}

export function useContact(id: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Contact>({
    queryKey: ['contacts', workspaceId, id],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Contact>(`/v1/contacts/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!id,
  });
}

export function useCreateContact() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateContactInput) => {
      const token = await getToken();
      return api.post<Contact>('/v1/contacts', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] });
    },
  });
}

export function useUpdateContact(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateContactInput) => {
      const token = await getToken();
      return api.patch<Contact>(`/v1/contacts/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] });
    },
  });
}

export function useDeleteContact() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/contacts/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] });
    },
  });
}
